/* eslint-disable no-underscore-dangle */
import React, { useCallback, useEffect, useState } from 'react';
import {
  Autocomplete,
  TextField,
  Typography,
  Box,
  Button,
  CircularProgress,
} from '@mui/material';
import useAxiosPrivate from '../../../../hooks/useAxiosPrivate';
import { ModalType } from '../../../../types/modal';
import { User, Document, Workspace } from '../../../../types/workspace';

interface EditWorkspaceProps {
  users: User[];
  documents: Document[];
  setShowModal: React.Dispatch<React.SetStateAction<boolean>>;
  setModalInfo: React.Dispatch<React.SetStateAction<ModalType>>;
}

const EditWorkspaces = ({
  users,
  documents,
  setShowModal,
  setModalInfo,
}: EditWorkspaceProps) => {
  const axiosPrivate = useAxiosPrivate();

  const [workspaces, setWorkspaces] = useState<Workspace[]>([]);
  const [selectedWorkspace, setSelectedWorkspace] = useState<Workspace | null>(
    null
  );
  const [workspaceName, setWorkspaceName] = useState<string>('');
  const [selectedUsers, setSelectedUsers] = useState<User[]>([]);
  const [selectedDocuments, setSelectedDocuments] = useState<Document[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const fetchWorkspaces = useCallback(async () => {
    try {
      const response = await axiosPrivate.get(
        '/admin/workspace/get-workspaces'
      );
      setWorkspaces(response.data);
    } catch (error) {
      console.error('Error fetching workspaces:', error);
    }
  }, [axiosPrivate]);

  useEffect(() => {
    fetchWorkspaces();
  }, [fetchWorkspaces]);

  useEffect(() => {
    if (selectedWorkspace) {
      setWorkspaceName(selectedWorkspace.workspace_name);
      setSelectedUsers(selectedWorkspace.users);
      setSelectedDocuments(selectedWorkspace.documents);
    }
  }, [selectedWorkspace]);

  const handleSelectWorkspace = useCallback(
    (event: React.SyntheticEvent, value: Workspace | null) => {
      setSelectedWorkspace(value);
      if (value) {
        setWorkspaceName(value.workspace_name);
      }
    },
    []
  );

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      if (!selectedWorkspace) return;

      const hasChanges =
        selectedWorkspace.workspace_name !== workspaceName ||
        JSON.stringify(selectedWorkspace.users) !==
          JSON.stringify(selectedUsers) ||
        JSON.stringify(selectedWorkspace.documents) !==
          JSON.stringify(selectedDocuments);

      if (!hasChanges) {
        setModalInfo({
          titleText: 'No Changes',
          bodyText: 'No changes detected',
          autoClose: true,
        });
        setShowModal(true);
        return;
      }

      setIsSubmitting(true);
      try {
        await axiosPrivate.put('/admin/workspace/update-workspace', {
          workspaceId: selectedWorkspace._id,
          name: workspaceName,
          users: selectedUsers,
          documents: selectedDocuments,
        });

        setSelectedWorkspace({
          ...selectedWorkspace,
          workspace_name: workspaceName,
          users: selectedUsers,
          documents: selectedDocuments,
        });

        setModalInfo({
          titleText: 'Success!',
          bodyText: `Workspace: ${workspaceName} updated successfully!`,
          autoClose: true,
        });
        setShowModal(true);
      } catch (error) {
        console.error('Error updating workspace:', error);
        setModalInfo({
          titleText: 'Error',
          bodyText: 'Failed to update workspace',
          autoClose: false,
        });
        setShowModal(true);
      } finally {
        setIsSubmitting(false);
      }
    },
    [
      axiosPrivate,
      selectedWorkspace,
      workspaceName,
      selectedUsers,
      selectedDocuments,
      setModalInfo,
      setShowModal,
    ]
  );

  const handleDeleteWorkspace = useCallback(async () => {
    if (!selectedWorkspace) return;

    try {
      await axiosPrivate.delete('/admin/workspace/delete-workspace', {
        data: { workspaceId: selectedWorkspace._id },
      });
      setWorkspaces((prevWorkspaces) =>
        prevWorkspaces.filter(
          (workspace) => workspace._id !== selectedWorkspace._id
        )
      );
      setSelectedWorkspace(null);
      setWorkspaceName('');
      setSelectedUsers([]);
      setSelectedDocuments([]);
      setModalInfo({
        titleText: 'Success!',
        bodyText: `Workspace: ${selectedWorkspace.workspace_name} deleted successfully!`,
        autoClose: true,
      });
      setShowModal(true);
    } catch (error) {
      console.error('Error deleting workspace:', error);
      setModalInfo({
        titleText: 'Error',
        bodyText: 'Failed to delete workspace',
        autoClose: false,
      });
      setShowModal(true);
    }
  }, [axiosPrivate, selectedWorkspace, setModalInfo, setShowModal]);

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Typography variant="h4" gutterBottom>
          Edit Workspaces
        </Typography>
        <Autocomplete
          sx={{ width: 300 }}
          options={workspaces}
          getOptionLabel={(option) => option.workspace_name}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Select Workspace"
              variant="outlined"
            />
          )}
          value={selectedWorkspace}
          onChange={handleSelectWorkspace}
        />
      </Box>
      {selectedWorkspace && (
        <Box component="form" onSubmit={handleSubmit} sx={{ mt: 3 }}>
          <Box sx={{ mb: 2 }}>
            <TextField
              label="Workspace Name"
              variant="outlined"
              fullWidth
              value={workspaceName}
              onChange={(e) => setWorkspaceName(e.target.value)}
              required
              disabled={selectedWorkspace.user_default_workspace}
            />
          </Box>
          <Box sx={{ mb: 2 }}>
            <Autocomplete
              multiple
              options={users}
              getOptionLabel={(user) => `${user.firstName} ${user.lastName}`}
              value={selectedUsers}
              onChange={(event, newValue: User[]) => setSelectedUsers(newValue)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="outlined"
                  label="Select Users"
                />
              )}
            />
          </Box>
          <Box sx={{ mb: 2 }}>
            <Autocomplete
              multiple
              options={documents}
              getOptionLabel={(doc) => doc.fileName}
              value={selectedDocuments}
              onChange={(event, newValue) => setSelectedDocuments(newValue)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="outlined"
                  label="Select Documents"
                />
              )}
            />
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'end', marginTop: 2 }}>
            <Box>
              <Button
                disabled={isSubmitting}
                type="button"
                variant="contained"
                color="error"
                onClick={handleDeleteWorkspace}
                sx={{ marginRight: 2 }}
              >
                Delete Workspace
              </Button>
              <Button
                disabled={isSubmitting}
                type="submit"
                variant="contained"
                color="primary"
                sx={{ marginLeft: 2 }}
              >
                Update Workspace
              </Button>
              {isSubmitting && <CircularProgress size={24} />}
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default EditWorkspaces;
